// @flow
import { css } from "styled-components";
import { ButtonLink as Button } from "@nested/component-library";
import {
  smallParagraph,
  media,
  mediumParagraph,
  VideoPlayer,
  ImageWindow,
} from "@nested/brand";
import {
  NestedLogoHouseOnly,
  ScallopedWindowDevice,
} from "@nested/brand/src/icons";

const background = css`
  background-color: ${({ theme }) => theme.palette.hague};
  padding: 20px;
  ${media.desktop`
    max-width: 1440px;
  `}
`;

const contentWrapper = css`
  ${media.tablet`
    max-width: 535px;
    margin: 0 auto;
  `}
  ${media.desktop`
    max-width: 1140px;
    margin: 0 auto;
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    padding: 5% 0;
    align-items: center;
  `}
`;

const textSection = css`
  ${media.desktop`
    width: calc(((100% + 40px) / 12 * 5) - 40px);
  `}
`;

const splitLogo = css`
  display: flex;
  margin-top: 15px;
`;

const logoStyles = css`
  box-sizing: border-box;
  padding-right: 15px;
  border-right: 1px solid rgba(255, 255, 255, 0.5);
  max-width: 140px;
  display: flex;
  align-items: center;
`;

const splitLogotext = css`
  margin-left: 15px;
  color: white;
  font-size: 11px;
  letter-spacing: 1.5px;
  line-height: 14px;
  opacity: 0.8;
  text-transform: uppercase;
  font-weight: 500;
  width: 50%;
  max-width: 200px;
`;

const aboutText = css`
  ${smallParagraph}
  color: white;
  margin: 15px 0 0;
  ${media.desktop`
    ${mediumParagraph}
    color: white;
    margin: 20px 0 0;
  `}
`;

const buttonContainer = css`
  margin-top: 20px;
  a:first-of-type {
    margin-right: 10px;
  }
  display: flex;
  a {
    flex-grow: 1;
    text-align: center;
  }
  ${media.tablet`
    display: block;
    a:first-of-type {
      margin-right: 20px;
    }
    a {
      padding: 0 30px;
    }
  `}
`;

const areasText = css`
  color: white;
  font-size: 12px;
  line-height: 18px;
  opacity: 0.8;
  margin: 15px 0 0;
  ${media.desktop`
    ${smallParagraph}
    margin: 15px 0;
    color: white;
  `}
`;

const videoWrapperStyles = css`
  width: 100%;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  ${media.desktop`
    width: calc(((100% + 40px)/ 12 * 5) - 40px);
    margin-right: calc(((100% + 40px) / 12));
    align-self: stretch;
  `}
`;

const videoWindowOverlay = css`
  height: 100%;
  width: 100%;
  z-index: 1;
  border: 2px solid ${({ theme }) => theme.palette.hague};
  display: block;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

type Props = {|
  area: string,
  areasCovered: string,
  heroImage: { url: string },
  telephone: string,
  videoLink?: string,
|};

export const LocalLandingPageHero = ({
  area,
  areasCovered,
  heroImage,
  telephone,
  videoLink,
}: Props) => (
  <div css={background}>
    <div css={contentWrapper}>
      {videoLink ? (
        <div css={videoWrapperStyles}>
          <VideoPlayer videoLink={videoLink} />
          <ScallopedWindowDevice css={videoWindowOverlay} />
        </div>
      ) : (
        <ImageWindow heroImage={heroImage?.url} />
      )}
      <div css={textSection}>
        <div css={splitLogo}>
          <div css={logoStyles}>
            <NestedLogoHouseOnly width="30px" />
          </div>
          <h1 css={splitLogotext}>
            Estate agent in
            <br />
            {area}
          </h1>
        </div>
        <p css={aboutText}>
          Nested is the only {area} estate agent that not only sells your
          current home, but also helps to negotiate tens of thousands off your
          new one.
        </p>
        <div css={buttonContainer}>
          <Button to={`tel:${telephone}`}>Call us</Button>
          <Button to="/get-started">Book a valuation</Button>
        </div>
        <p css={areasText}>
          Nested’s {area} branch covers {areasCovered}.
        </p>
      </div>
    </div>
  </div>
);
